import React, { useCallback, useEffect, useState } from 'react';

import { listAiOutputReports, reviewAiOutputReport, type AiOutputReport } from './api';
import { onlyDate } from './money';

/**
 * AI reports: AI output people flagged in the app, with the text as they saw
 * it. Google Play expects these to be read and to inform what the AI is
 * allowed to say; mark one reviewed once it has been dealt with.
 */

const REASON: Record<AiOutputReport['reason'], string> = {
  offensive: 'Offensive',
  harmful: 'Harmful or unsafe',
  wrong: 'Wrong or misleading',
  other: 'Other',
};

export default function AiReports({ toast }: { toast: (message: string) => void }) {
  const [rows, setRows] = useState<AiOutputReport[] | null>(null);
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(async () => {
    try {
      setRows(await listAiOutputReports(showAll ? null : 'open'));
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e));
      setRows([]);
    }
  }, [showAll, toast]);
  useEffect(() => void load(), [load]);

  if (rows === null) return <p className="muted">Loading reports…</p>;

  return (
    <>
      <div className="row" style={{ gap: 8, marginBottom: 12 }}>
        <button className={`chip ${showAll ? '' : 'active'}`} onClick={() => setShowAll(false)}>Open</button>
        <button className={`chip ${showAll ? 'active' : ''}`} onClick={() => setShowAll(true)}>All</button>
      </div>
      {rows.length === 0 ? (
        <p className="muted">{showAll ? 'Nobody has reported AI output.' : 'No open reports.'}</p>
      ) : (
        <div className="card">
          <table className="matrix">
            <thead>
              <tr><th>Reported</th><th>Why</th><th>Where</th><th>What the AI said</th><th>Who</th><th /></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} style={r.status === 'reviewed' ? { opacity: 0.6 } : undefined}>
                  <td>{onlyDate(r.created_at)}</td>
                  <td>{REASON[r.reason] ?? r.reason}</td>
                  <td><code>{r.surface}</code></td>
                  <td style={{ whiteSpace: 'pre-wrap', maxWidth: 480 }}>{r.output}</td>
                  <td>
                    {r.reporter_email ?? '—'}
                    {r.workspace_name ? <div className="muted" style={{ fontSize: 12 }}>{r.workspace_name}</div> : null}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {r.status === 'open' ? (
                      <button
                        className="btn"
                        onClick={async () => {
                          try {
                            await reviewAiOutputReport(r.id);
                            await load();
                          } catch (e) {
                            toast(e instanceof Error ? e.message : String(e));
                          }
                        }}
                      >
                        Mark reviewed
                      </button>
                    ) : (
                      <span className="muted">Reviewed</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
